<template>
    <modal size="lg" :height-max="false" @close="reset(); $emit('close')" ref="modal" :is-closeable="isCloseable" id="editDeliveryModal">
        <template v-slot:title>
            <h3 class="modal-title">Edit Delivery {{ editModePretty }}</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Company: </span>{{ delivery.company.name }}</div>
                    <div><span class="text-muted">Delivery: </span>{{ delivery.name }}</div>
                </div>
            </div>

            <form-errors :errors="formErrors"/>

            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-body">
                            <form v-if="editMode == 'address'">
                                <form-input label="Care of" v-model="formData.care_of" :errors="errors.care_of" type="text" :maxlength="100"/>
                                <form-input label="Address 1" v-model="formData.address_1" :errors="errors.address_1" type="text" :maxlength="100"/>
                                <form-input label="Address 2 (Optional)" v-model="formData.address_2" :errors="errors.address_2" type="text" :maxlength="100"/>
                                <form-input label="City" v-model="formData.city" :errors="errors.city" type="text" :maxlength="100"/>
                                <div class="form-row">
                                    <div class="col-6">
                                        <form-input label="State" v-model="formData.state" :errors="errors.state" type="select" :options="stateOptions"/>
                                    </div>

                                    <div class="col-6">
                                        <form-input label="Zip Code" :maxlength="5" v-model="formData.postal_code" :errors="errors.postal_code" type="text" inputmode="tel" />
                                    </div>
                                </div>
                                <form-input label="Friendly Name" v-model="formData.name" :errors="errors.name" type="text" :maxlength="100"/>
                                <form-input label="Use this as the default delivery address for this company" v-model="formData.is_default" :errors="errors.is_default" type="checkbox" class="mt-2"/>
                            </form>

                            <form v-else-if="editMode == 'method'">
                                <p>Select this company's preferred <strong>carrier</strong>:</p>
                                <form-input type="radio" v-model="formData.carrier" @input="onCarrierSelect" :errors="errors.carrier" :options="deliveryCarrierOptions" class="pl-0"/>
                                <form-input label="Method" v-if="deliveryMethodOptionsByCarrier[formData.carrier].length" type="select" v-model="formData.method" :errors="errors.method" :options="deliveryMethodOptionsByCarrier[formData.carrier]" class="mt-1 mb-2"/>

                                <template v-if="formData.carrier == 'ups' || formData.carrier == 'fedex' ">
                                    <p>Whose account will this company use with this carrier?</p>
                                    <div class="form-check-set">
                                        <div class="form-check d-inline-block mr-2">
                                            <label>
                                                <input name="use_own_account" type="radio" @click="formData.use_own_account = false" :checked="formData.use_own_account == false" class="form-check-input">
                                                Paymasters
                                            </label>
                                        </div>
                                        <div class="form-check d-inline-block">
                                            <label>
                                                <input name="use_own_account" type="radio" @click="formData.use_own_account = true" :checked="formData.use_own_account == true" class="form-check-input">
                                                Their own account
                                            </label>
                                        </div>
                                    </div>
                                    <div v-if="formData.use_own_account" class="mt-1">
                                        <form-input label="Name on the Account" v-model="formData.account_name" :errors="errors.account_name" type="text" :maxlength="100"/>
                                        <form-input label="Account Number" v-model="formData.account_number" :errors="errors.account_number" type="text" :maxlength="100"/>
                                    </div>
                                </template>

                                <template v-if="formData.carrier == 'courier'">
                                    <form-input label="Instructions" v-model="formData.instructions" :errors="errors.instructions" type="text" :nocaps="true"/>
                                </template>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import STATES from '@/states'

const deliveryCarrierOptions = [
    {text: 'UPS', value: 'ups'},
    {text: 'FedEx', value: 'fedex'},
    {text: 'Courier (ProMed)', value: 'courier'},
    {text: 'Pickup', value: 'pickup'},
]

const deliveryMethodOptionsByCarrier = {
    'fedex': [
        {text: 'FedEx Standard Overnight', value: 'fedex-standard-overnight'},
        {text: 'FedEx 2Day', value: 'fedex-2day'},
        {text: 'FedEx Ground', value: 'fedex-ground'},
    ],
    'ups': [
        {text: 'UPS Standard Overnight', value: 'ups-standard-overnight'},
        {text: 'UPS 2Day', value: 'ups-2day'},
        {text: 'UPS Ground', value: 'ups-ground'},
    ],
    'courier': [],
    'pickup': [],
    '': [],
}

export default {
    props: ['client', 'delivery', 'editMode'],
    mixins: [FormMixin, ModalMixin],
    data() {
        return {
            formData: this.makeFormData(),
            deliveryCarrierOptions: deliveryCarrierOptions,
            deliveryMethodOptionsByCarrier: deliveryMethodOptionsByCarrier,
        }
    },
    watch: {
        delivery() {
            this.reset()
        },
        editMode() {
            this.reset()
        },
    },
    computed: {
        stateOptions() {
            const opts = []
            STATES.forEach(s => {
                opts.push({text: s.name.toUpperCase(), value: s.abbreviation})
            })
            return opts
        },
        editModePretty() {
            if (!this.editMode) {
                return ''
            }
            return {
                'address': 'Address',
                'method': 'Method',
            }[this.editMode]
        },
    },
    methods: {
        makeFormData() {
            let delivery = JSON.parse(JSON.stringify(this.delivery))
            let data = {}
            if (this.editMode == 'address') {
                data = {
                    care_of: delivery.care_of,
                    name: delivery.name,
                    address_1: delivery.address_1,
                    address_2: delivery.address_2,
                    city: delivery.city,
                    state: delivery.state,
                    postal_code: delivery.postal_code,
                    is_default: delivery.is_default,
                    edit_mode: this.editMode,
                }
            } else if (this.editMode == 'method') {
                data = {
                    carrier: delivery.carrier,
                    method: delivery.method,
                    use_own_account: delivery.use_own_account,
                    account_name: delivery.account_name,
                    account_number: delivery.account_number,
                    instructions: delivery.instructions,
                    edit_mode: this.editMode,
                }
            }
            return data
        },
        onCarrierSelect() {
            this.formData.method = ''
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.patch(`/clients/${this.client.id}/settings/delivery-addresses/${this.delivery.id}`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated', resp)
                this.close()
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors
                this.formErrors = errors.__all__
                this.onError()
            })
        },
    },
}
</script>
